import React, { RefObject } from 'react';
import queryString from 'query-string';

import { AsyncTypeahead } from 'react-bootstrap-typeahead';
import { TFunction, useT } from 'i18n/hooks/useT';
import { useLocation } from 'react-router-dom';
import {
  SearchSection,
  SearchSectionBackground,
  SearchContent,
  SearchHeading,
} from './styles';
import TypeaheadOption from './components/TypeaheadOption';
import {
  ImportForm,
  TypeaheadOption as TypeaheadOptionProps,
} from './components/ImportForm';

type ImportScreenProps = {
  onSubmit: (partnerName?: string) => void;
  isSearching: boolean;
  onSelectTypeahead: (options: TypeaheadOptionProps[], t: TFunction) => void;
  onTypeaheadSearch: (value: string) => void;
  rssTypeaheadResults: TypeaheadOptionProps[];
  rssFeedError: {
    message: string;
  };
  fetchingRSSFeedImport: boolean;
  rssFeedIsValid: boolean;
  rssFeedMetadata: {
    authorName: string;
    authorEmail: string;
    image: string;
    isExplicit: boolean;
    itunesCategory: string;
    language: string;
    podcastName: string;
    podcastDescription: string;
    episodeCount: number;
  };
  rssFeedUrl: string;
  autoFocus?: boolean;
  inputRef?: RefObject<AsyncTypeahead<TypeaheadOptionProps>>;
};

export function ImportSection({
  isSearching,
  rssTypeaheadResults,
  onTypeaheadSearch,
  onSelectTypeahead,
  onSubmit,
  fetchingRSSFeedImport,
  rssFeedUrl,
  rssFeedIsValid,
  rssFeedError,
  rssFeedMetadata,
  autoFocus,
  inputRef,
}: ImportScreenProps) {
  const t = useT();
  const { search } = useLocation();
  const switchParams = queryString.parse(decodeURIComponent(search));

  const hasDataToContinue: boolean =
    !fetchingRSSFeedImport && rssFeedMetadata && !!rssFeedMetadata.podcastName;

  return (
    <SearchSection>
      <SearchSectionBackground>
        <SearchContent>
          <SearchHeading>
            {t(
              'ImportScreen.ImportSection.Header',
              'Switch in minutes',
              'Switch to Anchor switch section header'
            )}
          </SearchHeading>
          <ImportForm
            autoFocus={autoFocus}
            isSearching={isSearching}
            onSelectTypeahead={onSelectTypeahead}
            onTypeaheadSearch={onTypeaheadSearch}
            rssTypeaheadResults={rssTypeaheadResults}
            renderTypeaheadOption={option => {
              const { image, podcastName, authorName } = option;
              return (
                <TypeaheadOption
                  image={image}
                  podcastName={podcastName}
                  authorName={authorName}
                />
              );
            }}
            rssFeedError={rssFeedError}
            hasDataToContinue={hasDataToContinue}
            fetchingRSSFeedImport={fetchingRSSFeedImport}
            rssFeedIsValid={rssFeedIsValid}
            onClickGetStarted={() =>
              switchParams?.partner
                ? onSubmit(switchParams.partner.toString())
                : onSubmit()
            }
            rssFeedMetadata={rssFeedMetadata}
            inputRef={inputRef}
          />
        </SearchContent>
      </SearchSectionBackground>
    </SearchSection>
  );
}
